import { useEffect, useId, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useFocusTrap } from '../../hooks/useFocusTrap';

/**
 * Focus moves to `initialFocusRef` (default: the title) on open, stays inside
 * the dialog while it is open, and returns on close to `returnFocusRef`
 * (default: the element that opened it).
 */
export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  initialFocusRef,
  returnFocusRef,
  describedBy,
  className = '',
  closeLabel = 'Close',
  testId,
}) {
  const panelRef = useRef(null);
  const titleRef = useRef(null);
  const titleId = useId();
  useFocusTrap(panelRef, isOpen, { initialFocusRef: initialFocusRef ?? titleRef, returnFocusRef });

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const sizeClass = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
  }[size];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeOnBackdrop ? onClose : undefined}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />

          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            aria-describedby={describedBy}
            data-testid={testId}
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
            className={`relative w-full ${sizeClass} bg-white dark:bg-elevated-dark-1 rounded-2xl shadow-soft-lg border border-gray-200 dark:border-white/10 overflow-hidden ${className}`}
          >
            {title && (
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/5">
                <h2
                  id={titleId}
                  ref={titleRef}
                  tabIndex={-1}
                  className="text-heading font-semibold text-gray-900 dark:text-gray-100 focus:outline-none"
                >
                  {title}
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-1 -mr-1 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                  aria-label={closeLabel}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}

            <div className="px-6 py-4 max-h-[calc(90vh-8rem)] overflow-y-auto">
              {children}
            </div>

            {footer && (
              <div className="px-6 py-4 border-t border-gray-200 dark:border-white/5">
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
